// frontend/src/pages/ResultsPage.jsx
// Results page for completed sessions — light/dark aware.

import { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProject, getProjectMessages, downloadProjectExport } from '../utils/api';
import { DarkModeContext } from '../App';

export default function ResultsPage() {
  const { projectId } = useParams();
  const { darkMode, toggleDarkMode } = useContext(DarkModeContext);
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [projectId]);

  const loadData = async () => {
    try {
      const [projectData, messagesData] = await Promise.all([
        getProject(projectId),
        getProjectMessages(projectId)
      ]);
      setProject(projectData);
      setMessages(messagesData);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load results:', err);
      setLoading(false);
    }
  };

  const getRecommendationStyle = (recommendation) => {
    if (!recommendation) return 'text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800';
    if (recommendation === 'NOT VIABLE') return 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30';
    if (recommendation.includes('FILE AS-IS')) return 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30';
    if (recommendation.includes('FIX MAJOR AND')) return 'text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/30';
    if (recommendation.includes('ADVERSE')) return 'text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30';
    if (recommendation.includes('MAJOR REVISION')) return 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30';
    return 'text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800';
  };

  const downloadText = (filename, content) => {
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const downloadFullLog = () => {
    const log = messages.map(m =>
      `=== ${m.agentId.toUpperCase()} (Round ${m.roundNum}, ${m.phase}) ===\n${m.content}\n`
    ).join('\n');
    downloadText(`patent-council-log-${projectId}.txt`, log);
  };

  const handleDownload = async (format) => {
    try { await downloadProjectExport(projectId, format); }
    catch (err) { alert('Download failed: ' + err.message); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-700 dark:text-gray-300">Loading...</div>
      </div>
    );
  }
  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-700 dark:text-gray-300">Project not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <nav className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="text-xl font-bold text-gray-900 dark:text-gray-100">Patent Council</Link>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"
                aria-label="Toggle dark mode"
              >
                {darkMode ? '☀️' : '🌙'}
              </button>
              <Link to="/dashboard" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 px-3 py-2 rounded-md text-sm font-medium">
                ← Back to dashboard
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">{project.inventionTitle}</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">
          Status: {project.status} | {project.convergenceReason}
        </p>

        <div className={`p-6 rounded-lg mb-8 ${getRecommendationStyle(project.filingRecommendation)}`}>
          <h2 className="text-2xl font-bold mb-2">Filing Recommendation</h2>
          <p className="text-lg">{project.filingRecommendation || 'Not available'}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Download Full Report</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Includes invention, parsed seed (if any), all issues raised, 16-item checklist verdict, design-around analysis, viability verdict, and the complete transcript.
          </p>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => handleDownload('md')} className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">Markdown (.md)</button>
            <button onClick={() => handleDownload('html')} className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">HTML (.html)</button>
            <button onClick={() => handleDownload('json')} className="bg-gray-600 text-white px-4 py-2 rounded text-sm hover:bg-gray-700">JSON (.json)</button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="text-sm text-gray-500 dark:text-gray-400">Convergence Score</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{project.convergenceScore}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="text-sm text-gray-500 dark:text-gray-400">Rounds Completed</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {project.currentRound}{project.maxRoundsCap ? ` / ${project.maxRoundsCap}` : ''}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="text-sm text-gray-500 dark:text-gray-400">Total Messages</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{messages.length}</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Full Conversation Log</h2>
            <button onClick={downloadFullLog} className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">
              Download Log (.txt)
            </button>
          </div>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {messages.map((msg, idx) => (
              <div key={idx} className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 py-2">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {msg.agentId} (Round {msg.roundNum}, {msg.phase})
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap mt-1">
                  {msg.content}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Project Details</h2>
          <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <div>
              <span className="font-medium">Created:</span> {new Date(project.createdAt).toLocaleString()}
            </div>
            <div>
              <span className="font-medium">Last Updated:</span> {new Date(project.updatedAt).toLocaleString()}
            </div>
            <div>
              <span className="font-medium">Invention Description:</span>
              <p className="mt-2 text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{project.inventionDescription}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
